import React, { useRef, useEffect } from "react";
import classNames from "classnames";

import { GRADE_SCALE, STORAGE_GRADE_SCALE, GRADE_COLORS } from "utils/enclosure/embodiedCarbon";

import Tooltip from "components/Tooltip";

import classes from "./style.module.scss";
import { useStateSelector } from "utils/field";

/** Layer share of total as 0–10 filled segments; null when value is missing or invalid. */
const getFilledSegmentCount = (value, total) => {
    if (value == null) return null;
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num)) return null;

    const percentage = total ? num / total : 0;
    return Math.min(10, Math.max(0, Math.round(percentage * 10)));
};

const PercentageBar = ({ filledCount, type = "emission" }) => {
    const percentageScale = type === "storage" ? STORAGE_GRADE_SCALE : GRADE_SCALE;
    const count = filledCount ?? -1;
    const prevCountRef = useRef(count);

    const goingBackward = count < prevCountRef.current;

    useEffect(() => {
        prevCountRef.current = count;
    }, [count]);

    return (
        <div className={classes.percentageBar}>
            {percentageScale.map(({ label }, i) => {
                const isActive = filledCount != null && i < filledCount;
                const delay = goingBackward ? (percentageScale.length - 1 - i) * 0.05 : i * 0.05;
                return (
                    <div
                        key={label}
                        className={`${classes.percentageSegment} ${isActive ? classes.active : ""}`}
                        style={{
                            backgroundColor: type === "storage" ? GRADE_COLORS["A+"] : GRADE_COLORS[label],
                            transitionDelay: `${delay}s`,
                        }}
                    />
                );
            })}
        </div>
    );
};

/** Format kg/m² for the strip. Missing values use em dash; non-finite numbers render as 0.00 (legacy safety). */
export const formatEmbodiedMetric = (v) => {
    if (v == null) return "—";
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isNaN(n) || !Number.isFinite(n)) return (0).toFixed(2);
    return n.toFixed(2);
};

const PercentageDisplay = ({
    emissions,
    storage,
    withBorder = false,
    borderHeight = "100%",
    borderColor = "#E0E0E0",
    customGap = "0.75rem",
    customPadding = "0 7px",
    hasMismatch = false,
    /** Use with framing / whole-window rows: avoids `height: 100%` stretching that shifts numbers vertically. */
    layout = "default",
}) => {
    const { totalEmissions = 0, totalStorage = 0 } = useStateSelector(`newForm.codeDrawer.fields.code_embC`) || {};

    const emissionFilledCount = getFilledSegmentCount(emissions, totalEmissions);
    const storageFilledCount = getFilledSegmentCount(storage, totalStorage);

    const rootClass =
        layout === "framingGroup"
            ? `${classes.percentageDisplay} ${classes.percentageDisplayFramingGroup}`
            : classes.percentageDisplay;

    /* Inline `padding` shorthand (e.g. "0 7px") zeros out padding-top and overrides the module class — set top explicitly after. */
    const rootStyle =
        layout === "framingGroup"
            ? { gap: customGap, padding: customPadding, paddingTop: 6, paddingBottom: 4 }
            : { gap: customGap, padding: customPadding };

    return (
        <div className={classNames(rootClass, { [classes.mismatch]: hasMismatch })} style={rootStyle}>
            <div
                className={classes.percentageColumn}
                data-tip="Embodied Emissions"
                data-for="embodiedCarbonEmissionsTip"
            >
                <span className={classes.percentageValue}>
                    {formatEmbodiedMetric(emissions)}{" "}
                    <span className={classes.percentageUnit}>
                        kgCO<sub>2</sub>/m<sup>2</sup>
                    </span>
                </span>
                <PercentageBar filledCount={emissionFilledCount} />
            </div>
            {withBorder && <div style={{ width: "1px", height: borderHeight, backgroundColor: borderColor }} />}
            <div className={classes.percentageColumn} data-tip="Storage" data-for="embodiedCarbonStorageTip">
                <span className={classes.percentageValue}>
                    {formatEmbodiedMetric(storage)}{" "}
                    <span className={classes.percentageUnit}>
                        kgCO<sub>2</sub>/m<sup>2</sup>
                    </span>
                </span>
                <PercentageBar filledCount={storageFilledCount} type="storage" />
            </div>
            <Tooltip id="embodiedCarbonEmissionsTip" />
            <Tooltip id="embodiedCarbonStorageTip" />
        </div>
    );
};

export default PercentageDisplay;
